import { useEffect, useState } from "react";
import {
  CertificateProgressResponse,
  getCertificateProgress,
  issueCertificateToken,
} from "../../services/certificate/certificate-api";

export interface BackendProgressShape {
  token: string | null;
  data: CertificateProgressResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Issues a cert HMAC token on mount, then fetches `/progress`. Refetches every
 * 30 s while the meeting is live; otherwise fetches once on mount. Used by
 * `<CpeProgressWidget />` when an `eventId` + `email` pair is provided.
 */
export function useBackendProgress(
  eventId: number | string | undefined,
  email: string | undefined,
  isLive: boolean,
): BackendProgressShape {
  const [token, setToken] = useState<string | null>(null);
  const [data, setData] = useState<CertificateProgressResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  // Issue the token once (per eventId+email pair).
  useEffect(() => {
    if (!eventId || !email) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await issueCertificateToken(eventId, email);
        if (!cancelled) setToken(t);
      } catch (err) {
        if (!cancelled) setError((err as Error).message || "Token failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, email]);

  // Fetch /progress whenever the token changes or tick advances.
  useEffect(() => {
    if (!eventId || !email || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await getCertificateProgress(eventId, email, token);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError((err as Error).message || "Fetch failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, email, token, tick]);

  // Live polling: bump tick every 30 s while the meeting is live.
  //
  // The dashboard's `isLive` prop is per-browser and can be stale. To avoid
  // the case where a stale `isLive=false` prevents us from ever learning the
  // truth, ALSO poll whenever the most recent backend response said
  // `meetingLive: true`. Either signal keeps the loop running; both being
  // false stops it (the meeting really has ended).
  const backendMeetingLive = data?.meetingLive === true;
  const shouldPoll = isLive || backendMeetingLive;
  useEffect(() => {
    if (!shouldPoll) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [shouldPoll]);

  return {
    token,
    data,
    loading,
    error,
    refetch: () => setTick((n) => n + 1),
  };
}
